import React, { useEffect } from 'react';
import { Shield, Zap, CloudRain, Leaf, CheckCircle, TrendingUp, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { benefits, businessInfo } from '../mockData';

const iconMap = {
  'shield': Shield,
  'zap': Zap,
  'cloud-rain': CloudRain,
  'leaf': Leaf,
  'check-circle': CheckCircle,
  'trending-up': TrendingUp
};

export const WhyMetalRoofingPage = () => {
  useEffect(() => {
    document.title = "Why Choose Metal Roofing | Dezire Metal Roofing Sydney";
  }, []);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_roof-booking-hub/artifacts/zrqyrs2h_b1af1cf4-190b-4caf-945d-73d8591fc9b9.jpeg')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Why Choose <span className="text-primary">Metal Roofing</span>?
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Discover the benefits of Australia's most durable and energy-efficient roofing solution.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon] || Shield;
              return (
                <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-primary/30">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Metal Roofing vs Traditional Materials
            </h2>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Feature</th>
                    <th className="px-6 py-4 text-center">Metal Roofing</th>
                    <th className="px-6 py-4 text-center">Tiles/Shingles</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-6 py-4 font-medium">Lifespan</td>
                    <td className="px-6 py-4 text-center text-primary font-semibold">40-70 years</td>
                    <td className="px-6 py-4 text-center text-gray-500">15-30 years</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Maintenance</td>
                    <td className="px-6 py-4 text-center text-primary font-semibold">Minimal</td>
                    <td className="px-6 py-4 text-center text-gray-500">Regular</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">Energy Efficiency</td>
                    <td className="px-6 py-4 text-center text-primary font-semibold">Excellent</td>
                    <td className="px-6 py-4 text-center text-gray-500">Moderate</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Weather Resistance</td>
                    <td className="px-6 py-4 text-center text-primary font-semibold">Superior</td>
                    <td className="px-6 py-4 text-center text-gray-500">Good</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">Eco-Friendly</td>
                    <td className="px-6 py-4 text-center text-primary font-semibold">100% Recyclable</td>
                    <td className="px-6 py-4 text-center text-gray-500">Limited</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Upgrade Your Roof?</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Experience the benefits of metal roofing for yourself. Contact us for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-inspection">
              <Button size="lg" className="bg-primary hover:bg-accent text-white">
                Book Free Inspection
              </Button>
            </Link>
            <a href="/call">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
                <Phone className="w-5 h-5 mr-2" />
                Call {businessInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
